import React from 'react';
import BlockType from 'src/enums/blockType';
import EdgeLabel from 'src/enums/edgeLabel';
import EdgeType from 'src/enums/edgeType';
import { Block } from 'src/types/shared/blocks';
import { Block as BlockV3 } from 'src/types/shared/blocksV3';
import { DeckAction } from '../context/DeckContext';
import { DeckAction as DeckActionV3 } from '../context/DeckContextV3';
import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from 'src/constants/coefficients';
import { MAX_STAIR_HEIGHT } from 'src/constants/limits';
import { constructStair } from 'src/pages/quotations/views/blocks/utils/getDefaultStairV3';
import { constructLanding } from 'src/pages/quotations/views/blocks/utils/getDefaultLandingV3';
import { getOppositeEdge } from 'src/pages/quotations/views/blocks/utils/edgeUtils';
import getBlockLabel from 'src/utils/getBlockLabel';

// Pure function to cascade updates (height, steps, landings) to children
export const getBlocksWithCascadedUpdates = (
	allBlocks: (Block | BlockV3)[],
	oldBlock: Block | BlockV3,
	updatedBlock: Block | BlockV3
): (Block | BlockV3)[] => {
	let currentBlocks = [...allBlocks];
	const xChange = (updatedBlock.position?.x || 0) - (oldBlock.position?.x || 0);
	const yChange = (updatedBlock.position?.y || 0) - (oldBlock.position?.y || 0);

	// Helper to update a single block structure (nested or flat)
	// Returns [updatedBlock, shouldCascade]
	const processChildBlock = (
		block: Block | BlockV3,
		parentBlock: Block | BlockV3
	): { newBlock: Block | BlockV3; shouldTriggerCascade: boolean } => {
		const newPosition = {
			x: (block?.position?.x || 0) + xChange,
			y: (block?.position?.y || 0) + yChange,
		};

		const newDimensions = { ...block?.dimensions };
		let updatedEdges = block?.edges;
		let hasChanges = false;
		let shouldTriggerCascade = false;

		if (block?.type === BlockType.LANDING) {
			const landingParentBlock = block.parentBlock;
			if (
				landingParentBlock &&
				landingParentBlock.id === updatedBlock.id &&
				landingParentBlock.segmentId
			) {
				let targetSegment;
				let parentEdgeLabel;
				for (const edge of updatedBlock.edges || []) {
					const foundSeg = edge.segments?.find(
						(s) => s.id === landingParentBlock.segmentId
					);
					if (foundSeg) {
						targetSegment = foundSeg;
						parentEdgeLabel = edge.label;
						break;
					}
				}

				if (targetSegment) {
					const isHorizontalEdge =
						parentEdgeLabel === EdgeLabel.TOP ||
						parentEdgeLabel === EdgeLabel.BOTTOM;

					const currentLength = isHorizontalEdge
						? newDimensions.width
						: newDimensions.depth;

					if (currentLength !== targetSegment.length) {
						if (isHorizontalEdge) {
							newDimensions.width = targetSegment.length;
						} else {
							newDimensions.depth = targetSegment.length;
						}

						const displayWidth = isHorizontalEdge
							? targetSegment.length
							: newDimensions.width;
						const displayHeight = isHorizontalEdge
							? newDimensions.depth
							: targetSegment.length;

						newDimensions.displayDimensions = {
							...newDimensions.displayDimensions,
							width: displayWidth,
							height: displayHeight,
						};

						hasChanges = true;
						shouldTriggerCascade = true;
						updatedEdges = (block.edges || []).map((edge) => {
							// Determine if this edge runs parallel to the attached parent segment
							// Top/Bottom edges map to width when attached to Top/Bottom parent edges.
							// But for Right/Left attachment, the child's Top/Bottom edges map to depth!
							const isWidthEdge = isHorizontalEdge
								? edge.label === EdgeLabel.TOP ||
									edge.label === EdgeLabel.BOTTOM
								: edge.label === EdgeLabel.LEFT ||
									edge.label === EdgeLabel.RIGHT;

							const newEdgeLength = isWidthEdge
								? targetSegment.length
								: isHorizontalEdge
									? newDimensions.depth
									: newDimensions.width;

							return {
								...edge,
								length: newEdgeLength,
								segments: edge.segments.map((seg) => ({
									...seg,
									length: newEdgeLength,
								})),
							};
						});
					}
				}
			}

			if (updatedBlock.type === BlockType.DECKBOX) {
				if (newDimensions.height !== updatedBlock.dimensions.height) {
					newDimensions.height = updatedBlock.dimensions.height;
					hasChanges = true;
					shouldTriggerCascade = true;
				}
			} else if (updatedBlock.type === BlockType.STAIR) {
				const stairEnd = updatedBlock.dimensions.endHeight || 0;
				if (newDimensions.height !== stairEnd) {
					newDimensions.height = stairEnd;
					hasChanges = true;
					shouldTriggerCascade = true;
				}
			}
		}

		// Cascade Height: Deckbox -> Stair
		if (block?.type === BlockType.STAIR) {
			// CRITICAL: Only cascade DeckBox height to DIRECT children.
			// Nested stairs (attached to Landings) should NOT inherit DeckBox height.
			if (
				updatedBlock.type === BlockType.DECKBOX &&
				block.parentBlock?.id !== updatedBlock.id
			) {
				// Do not process height for indirect children
			} else {
				const oldStartHeight = block.dimensions.startHeight || 0;
				const newStart = updatedBlock.dimensions.height || 0;

				if (newStart !== oldStartHeight) {
					newDimensions.startHeight = newStart;
					newDimensions.height = newStart - (block.dimensions.endHeight || 0);
					newDimensions.numberOfSteps = Math.ceil(
						newDimensions.height / STEPS_COEFFICIENT
					);
					newDimensions.depth = newDimensions.numberOfSteps * DEPTH_COEFFICIENT;
					hasChanges = true;
					shouldTriggerCascade = true;
					const _cascadeAttachedEdge = block.edges?.find(
						(e) => e.attachedToParent
					);
					const _cascadeEndLabel = _cascadeAttachedEdge
						? getOppositeEdge(_cascadeAttachedEdge.label as string)
						: null;
					updatedEdges = (block.edges || []).map((e) => {
						if (e.attachedToParent || e.label === _cascadeEndLabel) return e;
						return {
							...e,
							length: newDimensions.depth,
							segments: e.segments.map((s) => ({
								...s,
								length: newDimensions.depth,
							})),
						};
					});
				}
			}
		}

		if (hasChanges || xChange !== 0 || yChange !== 0) {
			const returnedBlock: any = {
				...block,
				position: newPosition,
				dimensions: newDimensions,
				edges: updatedEdges,
			};
			if (
				block.type === BlockType.STAIR &&
				newDimensions.depth !== block.dimensions?.depth
			) {
				const _retAttachedEdge = block.edges?.find((e) => e.attachedToParent);
				const _retIsTopBottom =
					_retAttachedEdge?.label === EdgeLabel.TOP ||
					_retAttachedEdge?.label === EdgeLabel.BOTTOM;
				returnedBlock.displayDimensions = {
					width: _retIsTopBottom ? newDimensions.width : newDimensions.depth,
					height: _retIsTopBottom ? newDimensions.depth : newDimensions.width,
				};
			}
			return {
				newBlock: returnedBlock,
				shouldTriggerCascade,
			};
		}

		return { newBlock: block, shouldTriggerCascade: false };
	};

	// We need to find children. They can be:
	// 1. Flat children (parentBlock.id matches)
	// 2. Nested children (inside stairs/landings arrays of updatedBlock)

	// --- Handle Flat Children ---
	let directFlatChildren = currentBlocks.filter(
		(blk) => blk?.parentBlock?.id === updatedBlock.id
	);

	// --- Handle Nested Children (if updatedBlock is DeckBox having arrays) ---
	let nestedStairs = (updatedBlock as Block).stairs || [];
	let nestedLandings = (updatedBlock as Block).landings || [];

	// Process Flat Children
	directFlatChildren.forEach((child) => {
		const { newBlock, shouldTriggerCascade } = processChildBlock(
			child,
			updatedBlock
		);
		if (newBlock !== child) {
			// Explicitly preserve ref for flat children
			newBlock.ref = child.ref;
			const index = currentBlocks.findIndex((b) => b.id === child.id);
			if (index !== -1) currentBlocks[index] = newBlock;

			if (shouldTriggerCascade) {
				currentBlocks = getBlocksWithCascadedUpdates(
					currentBlocks,
					child,
					newBlock
				);
			}
		}
	});

	// Process Nested Children (Stairs AND Landings)
	// We must separate the loops but track changes unifiedly
	const newStairs: Block[] = [];
	const newLandings: Block[] = [];
	let hasNestedChanges = false;

	if (nestedStairs.length > 0) {
		nestedStairs.forEach((stair) => {
			let { newBlock: updatedStair, shouldTriggerCascade } = processChildBlock(
				stair,
				updatedBlock
			);

			updatedStair.position = stair.position;
			updatedStair.ref = stair.ref;

			if (updatedStair.type === BlockType.STAIR) {
				const oldStartHeight = stair.dimensions.startHeight || 0;
				const newStartHeight = updatedStair.dimensions.startHeight || 0;

				// Identify potential Intermediate Landing (in deckbox.landings with parentBlock.id == stair.id)
				const parentDeckboxLandings = nestedLandings;
				const childLandings = parentDeckboxLandings.filter(
					(l) => l.parentBlock.id === stair.id
				);
				const hasIntermediateLanding = childLandings.length > 0;

				if (oldStartHeight !== newStartHeight) {
					// REMOVE
					if (newStartHeight <= MAX_STAIR_HEIGHT && hasIntermediateLanding) {
						const landingToRemove = childLandings[0];
						const stairBToRemove = nestedStairs.find(
							(s) => s.parentBlock.id === landingToRemove.id
						);

						(updatedBlock as any)._idsToRemove =
							(updatedBlock as any)._idsToRemove || [];
						(updatedBlock as any)._idsToRemove.push(landingToRemove.id);
						if (stairBToRemove)
							(updatedBlock as any)._idsToRemove.push(stairBToRemove.id);

						updatedStair.dimensions.endHeight = 0;
						updatedStair.dimensions.height = newStartHeight;
						updatedStair.dimensions.numberOfSteps = Math.ceil(
							newStartHeight / STEPS_COEFFICIENT
						);
						updatedStair.dimensions.depth =
							updatedStair.dimensions.numberOfSteps * DEPTH_COEFFICIENT;
						const _removeAttachedEdge = updatedStair.edges?.find(
							(e) => e.attachedToParent
						);
						const _removeIsTopBottom =
							_removeAttachedEdge?.label === EdgeLabel.TOP ||
							_removeAttachedEdge?.label === EdgeLabel.BOTTOM;
						updatedStair.displayDimensions = {
							width: _removeIsTopBottom
								? updatedStair.dimensions.width
								: updatedStair.dimensions.depth,
							height: _removeIsTopBottom
								? updatedStair.dimensions.depth
								: updatedStair.dimensions.width,
						};
						hasNestedChanges = true;
						updatedStair.position = stair.position;

						// Revert edge to GROUND
						if (updatedStair.edges) {
							const _removeDepth = updatedStair.dimensions.depth;
							updatedStair.edges = updatedStair.edges.map((e) => {
								if (e.attachedToParent) return e;
								const hasAttachedSegment = e.segments.some(
									(s) => s.type === EdgeType.LANDING_ATTACHED
								);
								if (hasAttachedSegment) {
									return {
										...e,
										segments: e.segments.map((s) => {
											if (s.type === EdgeType.LANDING_ATTACHED) {
												return { ...s, type: EdgeType.GROUND };
											}
											return s;
										}),
									};
								}
								return {
									...e,
									length: _removeDepth,
									segments: e.segments.map((s) => ({
										...s,
										length: _removeDepth,
									})),
								};
							});
						}
					}

					// ADD
					if (
						oldStartHeight <= MAX_STAIR_HEIGHT &&
						newStartHeight > MAX_STAIR_HEIGHT &&
						!hasIntermediateLanding
					) {
						const halfHeight = Math.ceil(newStartHeight / 2);
						updatedStair.dimensions.endHeight = halfHeight;
						updatedStair.dimensions.height = halfHeight;
						updatedStair.dimensions.numberOfSteps = Math.ceil(
							halfHeight / STEPS_COEFFICIENT
						);
						updatedStair.dimensions.depth =
							updatedStair.dimensions.numberOfSteps * DEPTH_COEFFICIENT;
						const _addAttachedEdge = updatedStair.edges?.find(
							(e) => e.attachedToParent
						);
						const _addIsTopBottom =
							_addAttachedEdge?.label === EdgeLabel.TOP ||
							_addAttachedEdge?.label === EdgeLabel.BOTTOM;
						updatedStair.displayDimensions = {
							width: _addIsTopBottom
								? updatedStair.dimensions.width
								: updatedStair.dimensions.depth,
							height: _addIsTopBottom
								? updatedStair.dimensions.depth
								: updatedStair.dimensions.width,
						};
						hasNestedChanges = true;

						// Add Landing + Stair B logic
						const attachmentEdge = updatedStair.edges?.find(
							(e) => e.attachedToParent
						);
						const endLabel = attachmentEdge
							? getOppositeEdge(attachmentEdge.label as string)
							: EdgeLabel.BOTTOM;

						const stairEndEdge = updatedStair.edges?.find(
							(e) => e.label === endLabel
						);
						const stairEndSegment = stairEndEdge?.segments?.[0];

						if (stairEndEdge && stairEndSegment) {
							if (updatedStair.edges) {
								const _addDepth = updatedStair.dimensions.depth;
								updatedStair.edges = updatedStair.edges.map((e) => {
									if (e.id === stairEndEdge.id) {
										return {
											...e,
											segments: e.segments.map((s) => {
												if (s.id === stairEndSegment.id) {
													return { ...s, type: EdgeType.LANDING_ATTACHED };
												}
												return s;
											}),
										};
									}
									if (e.attachedToParent) return e;
									return {
										...e,
										length: _addDepth,
										segments: e.segments.map((s) => ({
											...s,
											length: _addDepth,
										})),
									};
								});
							}

							const updatedSegmentForLanding = {
								...stairEndSegment,
								type: EdgeType.LANDING_ATTACHED,
							};
							const newLanding = constructLanding({
								parentBlock: updatedStair,
								selectedBlockEdge: stairEndEdge,
								vacantSegment: updatedSegmentForLanding,
								defaultSize: 48,
								label: getBlockLabel([updatedBlock as Block], {
									type: BlockType.LANDING,
								} as Block),
							});

							(updatedBlock as any)._itemsToAdd =
								(updatedBlock as any)._itemsToAdd || [];
							(updatedBlock as any)._itemsToAdd.push({
								type: 'LANDING',
								block: newLanding,
							});

							const landingAttachmentEdge = newLanding.edges.find(
								(e) => e.attachedToParent
							);
							const landingEndLabel = landingAttachmentEdge
								? getOppositeEdge(landingAttachmentEdge.label as string)
								: EdgeLabel.BOTTOM;
							const landingEndEdge = newLanding.edges.find(
								(e) => e.label === landingEndLabel
							);
							const landingEndSegment = landingEndEdge?.segments?.[0];

							if (landingEndEdge && landingEndSegment) {
								newLanding.edges = newLanding.edges.map((e) => {
									if (e.id !== landingEndEdge.id) return e;
									return {
										...e,
										segments: e.segments.map((s) =>
											s.id === landingEndSegment.id
												? { ...s, type: EdgeType.STAIR_ATTACHED }
												: s
										),
									};
								});
								const { defaultStair: newStairB } = constructStair({
									parentBlock: newLanding,
									selectedBlockEdge: landingEndEdge,
									vacantSegment: landingEndSegment,
									label: getBlockLabel([updatedBlock as Block], {
										type: BlockType.STAIR,
									} as Block),
								});
								(updatedBlock as any)._itemsToAdd.push({
									type: 'STAIR',
									block: newStairB,
								});
							}
						}
					}
				}
			}

			newStairs.push(updatedStair as Block);
			if (updatedStair !== stair) hasNestedChanges = true;
		});
	}

	// Process Nested Landings
	if (nestedLandings.length > 0) {
		nestedLandings.forEach((landing) => {
			let updatedLanding: Block | BlockV3;

			// If this landing's actual parent is a stair (not the deckbox), use that
			// stair's new endHeight instead of the deckbox height.
			const parentStairInNested = newStairs.find(
				(s) => s.id === landing.parentBlock?.id
			);

			if (parentStairInNested) {
				// Determine whether this landing is on the stair's end edge
				// (tracks endHeight) or a side edge (tracks startHeight).
				const _stairAE = parentStairInNested.edges.find(
					(e) => e.attachedToParent
				);
				const _stairEndLabel = _stairAE
					? getOppositeEdge(_stairAE.label as string)
					: null;
				const _landingEdge = parentStairInNested.edges.find(
					(e) => e.id === landing.parentBlock?.edgeId
				);
				const _isEndEdge = _landingEdge?.label === _stairEndLabel;
				const targetHeight = _isEndEdge
					? parentStairInNested.dimensions.endHeight != null
						? parentStairInNested.dimensions.endHeight
						: parentStairInNested.dimensions.startHeight ||
							parentStairInNested.dimensions.height ||
							0
					: parentStairInNested.dimensions.startHeight ||
						parentStairInNested.dimensions.height ||
						0;
				const currentHeight = landing.dimensions?.height ?? 0;
				const heightChanged = currentHeight !== targetHeight;
				updatedLanding = heightChanged
					? {
							...landing,
							dimensions: { ...landing.dimensions, height: targetHeight },
						}
					: { ...landing };
				if (heightChanged) hasNestedChanges = true;
			} else {
				const { newBlock } = processChildBlock(landing, updatedBlock);
				updatedLanding = newBlock;
				if (updatedLanding !== landing) hasNestedChanges = true;
			}

			updatedLanding.position = landing.position;
			updatedLanding.ref = landing.ref;
			newLandings.push(updatedLanding as Block);
		});
	}

	if (newLandings.length > 0 && newStairs.length > 0) {
		const toRemoveIds = new Set<string>(
			((updatedBlock as any)._idsToRemove || []) as string[]
		);
		newLandings.forEach((updatedLanding) => {
			const originalLanding = nestedLandings.find(
				(l) => l.id === updatedLanding.id
			);
			if (!originalLanding) return;
			if (
				(updatedLanding.dimensions?.height ?? 0) ===
				(originalLanding.dimensions?.height ?? 0)
			)
				return;

			for (let i = 0; i < newStairs.length; i++) {
				const stair = newStairs[i];
				if (stair.parentBlock?.id !== updatedLanding.id) continue;
				if (toRemoveIds.has(stair.id as string)) continue;

				const newStartHeight = updatedLanding.dimensions.height;
				if (stair.dimensions.startHeight === newStartHeight) continue;

				const newHeight = newStartHeight - (stair.dimensions.endHeight || 0);
				const numberOfSteps = Math.ceil(newHeight / STEPS_COEFFICIENT);
				const depth = numberOfSteps * DEPTH_COEFFICIENT;

				const attachedEdge = (stair.edges || []).find(
					(e) => e.attachedToParent
				);
				const isTopBottom =
					attachedEdge?.label === EdgeLabel.TOP ||
					attachedEdge?.label === EdgeLabel.BOTTOM;

				const endLabel = attachedEdge
					? getOppositeEdge(attachedEdge.label as string)
					: null;
				const updatedEdges = (stair.edges || []).map((e) => {
					if (e.attachedToParent || e.label === endLabel) return e;
					return {
						...e,
						length: depth,
						segments: e.segments.map((s) => ({ ...s, length: depth })),
					};
				});

				newStairs[i] = {
					...stair,
					dimensions: {
						...stair.dimensions,
						startHeight: newStartHeight,
						height: newHeight,
						numberOfSteps,
						depth,
					},
					displayDimensions: {
						width: isTopBottom ? stair.dimensions.width : depth,
						height: isTopBottom ? depth : stair.dimensions.width,
					},
					edges: updatedEdges,
				} as Block;
				hasNestedChanges = true;
			}
		});
	}

	for (let i = 0; i < newStairs.length; i++) {
		const updatedStair = newStairs[i];
		const originalStair = nestedStairs.find((s) => s.id === updatedStair.id);
		if (!originalStair) continue;

		const oldStairStart = originalStair.dimensions.startHeight || 0;
		const newStairStart = updatedStair.dimensions.startHeight || 0;

		if (
			newStairStart === oldStairStart ||
			updatedStair.parentBlock?.id === updatedBlock.id
		)
			continue;

		const childLandingsOfStair = newLandings.filter(
			(l) => l.parentBlock?.id === updatedStair.id
		);
		const hasIntermediateLanding = childLandingsOfStair.length > 0;

		if (
			oldStairStart <= MAX_STAIR_HEIGHT &&
			newStairStart > MAX_STAIR_HEIGHT &&
			!hasIntermediateLanding
		) {
			const halfHeight = Math.ceil(newStairStart / 2);
			const halfSteps = Math.ceil(halfHeight / STEPS_COEFFICIENT);
			const halfDepth = halfSteps * DEPTH_COEFFICIENT;

			const attachmentEdge = updatedStair.edges?.find(
				(e) => e.attachedToParent
			);
			const isTopBottomAdd =
				attachmentEdge?.label === EdgeLabel.TOP ||
				attachmentEdge?.label === EdgeLabel.BOTTOM;

			newStairs[i] = {
				...updatedStair,
				dimensions: {
					...updatedStair.dimensions,
					endHeight: halfHeight,
					height: halfHeight,
					numberOfSteps: halfSteps,
					depth: halfDepth,
				},
				displayDimensions: {
					width: isTopBottomAdd ? updatedStair.dimensions.width : halfDepth,
					height: isTopBottomAdd ? halfDepth : updatedStair.dimensions.width,
				},
			} as Block;

			const endLabel = attachmentEdge
				? getOppositeEdge(attachmentEdge.label as string)
				: EdgeLabel.BOTTOM;
			const stairEndEdge = newStairs[i].edges?.find(
				(e) => e.label === endLabel
			);
			const stairEndSegment = stairEndEdge?.segments?.[0];

			if (stairEndEdge && stairEndSegment) {
				newStairs[i] = {
					...newStairs[i],
					edges: (newStairs[i].edges || []).map((e) => {
						if (e.id === stairEndEdge.id) {
							return {
								...e,
								segments: e.segments.map((s) => {
									if (s.id === stairEndSegment.id) {
										return { ...s, type: EdgeType.LANDING_ATTACHED };
									}
									return s;
								}),
							};
						}
						if (e.attachedToParent) return e;
						return {
							...e,
							length: halfDepth,
							segments: e.segments.map((s) => ({ ...s, length: halfDepth })),
						};
					}),
				} as Block;

				const updatedSegmentForLanding = {
					...stairEndSegment,
					type: EdgeType.LANDING_ATTACHED,
				};
				const newInterLanding = constructLanding({
					parentBlock: newStairs[i],
					selectedBlockEdge: stairEndEdge,
					vacantSegment: updatedSegmentForLanding,
					defaultSize: 48,
					label: getBlockLabel([updatedBlock as Block], {
						type: BlockType.LANDING,
					} as Block),
				});

				(updatedBlock as any)._itemsToAdd =
					(updatedBlock as any)._itemsToAdd || [];
				(updatedBlock as any)._itemsToAdd.push({
					type: 'LANDING',
					block: newInterLanding,
				});

				const landingAttachEdge = newInterLanding.edges.find(
					(e) => e.attachedToParent
				);
				const landingEndLabel = landingAttachEdge
					? getOppositeEdge(landingAttachEdge.label as string)
					: EdgeLabel.BOTTOM;
				const landingEndEdge = newInterLanding.edges.find(
					(e) => e.label === landingEndLabel
				);
				const landingEndSegment = landingEndEdge?.segments?.[0];

				if (landingEndEdge && landingEndSegment) {
					// Mark landing's end-edge segment as STAIR_ATTACHED
					newInterLanding.edges = newInterLanding.edges.map((e) => {
						if (e.id !== landingEndEdge.id) return e;
						return {
							...e,
							segments: e.segments.map((s) =>
								s.id === landingEndSegment.id
									? { ...s, type: EdgeType.STAIR_ATTACHED }
									: s
							),
						};
					});
					const { defaultStair: newStairB } = constructStair({
						parentBlock: newInterLanding,
						selectedBlockEdge: landingEndEdge,
						vacantSegment: landingEndSegment,
						label: getBlockLabel([updatedBlock as Block], {
							type: BlockType.STAIR,
						} as Block),
					});
					(updatedBlock as any)._itemsToAdd.push({
						type: 'STAIR',
						block: newStairB,
					});
				}
			}

			hasNestedChanges = true;
		}

		if (newStairStart <= MAX_STAIR_HEIGHT && hasIntermediateLanding) {
			const landingToRemove = childLandingsOfStair[0];
			const stairBToRemove = newStairs.find(
				(s) => s.parentBlock?.id === landingToRemove.id
			);

			(updatedBlock as any)._idsToRemove =
				(updatedBlock as any)._idsToRemove || [];
			(updatedBlock as any)._idsToRemove.push(landingToRemove.id);
			if (stairBToRemove)
				(updatedBlock as any)._idsToRemove.push(stairBToRemove.id);

			newStairs[i] = {
				...newStairs[i],
				dimensions: {
					...newStairs[i].dimensions,
					endHeight: 0,
					height: newStairStart,
					numberOfSteps: Math.ceil(newStairStart / STEPS_COEFFICIENT),
					depth:
						Math.ceil(newStairStart / STEPS_COEFFICIENT) * DEPTH_COEFFICIENT,
				},
			} as Block;

			if (newStairs[i].edges) {
				const _indRemoveDepth = newStairs[i].dimensions.depth;
				newStairs[i] = {
					...newStairs[i],
					edges: (newStairs[i].edges || []).map((e) => {
						if (e.attachedToParent) return e;
						const hasAttachedSeg = e.segments.some(
							(s) => s.type === EdgeType.LANDING_ATTACHED
						);
						if (hasAttachedSeg) {
							return {
								...e,
								segments: e.segments.map((s) => {
									if (s.type === EdgeType.LANDING_ATTACHED) {
										return { ...s, type: EdgeType.GROUND };
									}
									return s;
								}),
							};
						}
						return {
							...e,
							length: _indRemoveDepth,
							segments: e.segments.map((s) => ({
								...s,
								length: _indRemoveDepth,
							})),
						};
					}),
				} as Block;
			}

			const _rAttachedEdge = newStairs[i].edges?.find(
				(e) => e.attachedToParent
			);
			const _rIsTopBottom =
				_rAttachedEdge?.label === EdgeLabel.TOP ||
				_rAttachedEdge?.label === EdgeLabel.BOTTOM;
			newStairs[i] = {
				...newStairs[i],
				displayDimensions: {
					width: _rIsTopBottom
						? newStairs[i].dimensions.width
						: newStairs[i].dimensions.depth,
					height: _rIsTopBottom
						? newStairs[i].dimensions.depth
						: newStairs[i].dimensions.width,
				},
			} as Block;

			hasNestedChanges = true;
		}
	}

	// Apply Changes to updatedBlock
	if (
		hasNestedChanges ||
		(updatedBlock as any)._idsToRemove ||
		(updatedBlock as any)._itemsToAdd
	) {
		const toRemove = (updatedBlock as any)._idsToRemove || [];
		const toAdd = (updatedBlock as any)._itemsToAdd || [];

		let finalStairs = (
			nestedStairs.length > 0 ? newStairs : nestedStairs
		).filter((s) => !toRemove.includes(s.id));
		let finalLandings = (
			nestedLandings.length > 0 ? newLandings : nestedLandings
		).filter((l) => !toRemove.includes(l.id));

		toAdd.forEach((item: any) => {
			if (item.type === 'STAIR') finalStairs.push(item.block);
			if (item.type === 'LANDING') finalLandings.push(item.block);
		});

		const idx = currentBlocks.findIndex((b) => b.id === updatedBlock.id);
		if (idx !== -1) {
			currentBlocks[idx] = {
				...currentBlocks[idx],
				stairs: finalStairs,
				landings: finalLandings,
			} as Block;
		}
	}

	if (updatedBlock.type === BlockType.STAIR) {
		const stairEndHeight = (updatedBlock as Block).dimensions.endHeight || 0;

		const deckboxIdx = currentBlocks.findIndex(
			(blk) =>
				blk.type === BlockType.DECKBOX &&
				((blk as Block).stairs || []).some((s) => s.id === updatedBlock.id)
		);

		if (deckboxIdx !== -1) {
			const deckbox = currentBlocks[deckboxIdx] as Block;
			let nestedChanged = false;

			const updatedDeckboxLandings = (deckbox.landings || []).map((landing) => {
				if (landing.parentBlock?.id !== updatedBlock.id) return landing;
				const _stairAE2 = (updatedBlock as Block).edges?.find(
					(e) => e.attachedToParent
				);
				const _stairEndLabel2 = _stairAE2
					? getOppositeEdge(_stairAE2.label as string)
					: null;
				const _landingEdge2 = (updatedBlock as Block).edges?.find(
					(e) => e.id === landing.parentBlock?.edgeId
				);
				const _isEndEdge2 = _landingEdge2?.label === _stairEndLabel2;
				const targetH = _isEndEdge2
					? stairEndHeight ||
						(updatedBlock as Block).dimensions.startHeight ||
						(updatedBlock as Block).dimensions.height ||
						0
					: (updatedBlock as Block).dimensions.startHeight ||
						(updatedBlock as Block).dimensions.height ||
						0;
				if (landing.dimensions.height === targetH) return landing;
				nestedChanged = true;
				return {
					...landing,
					dimensions: { ...landing.dimensions, height: targetH },
				};
			});

			const updatedDeckboxStairs = (deckbox.stairs || []).map((stair) => {
				if (stair.id === updatedBlock.id) return stair;
				const parentLanding = updatedDeckboxLandings.find(
					(l) => l.id === stair.parentBlock?.id
				);
				if (!parentLanding) return stair;
				const originalLanding = (deckbox.landings || []).find(
					(l) => l.id === parentLanding.id
				);
				if (
					!originalLanding ||
					parentLanding.dimensions.height === originalLanding.dimensions.height
				)
					return stair;

				const newStartHeight = parentLanding.dimensions.height;
				if (stair.dimensions.startHeight === newStartHeight) return stair;

				const newHeight = newStartHeight - (stair.dimensions.endHeight || 0);
				const numberOfSteps = Math.ceil(newHeight / STEPS_COEFFICIENT);
				const depth = numberOfSteps * DEPTH_COEFFICIENT;

				const attachedEdge = (stair.edges || []).find(
					(e) => e.attachedToParent
				);
				const isTopBottom =
					attachedEdge?.label === EdgeLabel.TOP ||
					attachedEdge?.label === EdgeLabel.BOTTOM;

				const endLabel2 = attachedEdge
					? getOppositeEdge(attachedEdge.label as string)
					: null;
				const updatedEdges2 = (stair.edges || []).map((e) => {
					if (e.attachedToParent || e.label === endLabel2) return e;
					return {
						...e,
						length: depth,
						segments: e.segments.map((s) => ({ ...s, length: depth })),
					};
				});

				nestedChanged = true;
				return {
					...stair,
					dimensions: {
						...stair.dimensions,
						startHeight: newStartHeight,
						height: newHeight,
						numberOfSteps,
						depth,
					},
					displayDimensions: {
						width: isTopBottom ? stair.dimensions.width : depth,
						height: isTopBottom ? depth : stair.dimensions.width,
					},
					edges: updatedEdges2,
				};
			});

			if (nestedChanged) {
				currentBlocks[deckboxIdx] = {
					...currentBlocks[deckboxIdx],
					stairs: updatedDeckboxStairs,
					landings: updatedDeckboxLandings,
				} as Block;
			}
		}
	}

	return currentBlocks;
};

/**
 * Wrapper that integrates with the Redux/Reducer dispatch.
 * It calculates cascaded updates and dispatches the necessary actions.
 */
const updateChildBlocks = ({
	blocks,
	oldBlock,
	updatedBlock,
	dispatch,
}: {
	blocks: (Block | BlockV3)[];
	oldBlock: Block | BlockV3;
	updatedBlock: Block | BlockV3;
	dispatch: React.Dispatch<DeckAction> | React.Dispatch<DeckActionV3>;
}) => {
	console.log('[updateChildBlocks] Called');
	const nextBlocks = getBlocksWithCascadedUpdates(
		blocks,
		oldBlock,
		updatedBlock
	);
	const parentAfterCascade = nextBlocks.find((b) => b.id === updatedBlock.id);

	if (!parentAfterCascade && updatedBlock.type === BlockType.STAIR) {
		const updatedDeckbox = nextBlocks.find(
			(blk) =>
				blk.type === BlockType.DECKBOX &&
				((blk as Block).stairs || []).some((s) => s.id === updatedBlock.id)
		) as Block | undefined;

		if (updatedDeckbox) {
			const oldDeckbox = blocks.find((b) => b.id === updatedDeckbox.id) as
				| Block
				| undefined;

			(updatedDeckbox.landings || []).forEach((landing) => {
				const oldLanding = (oldDeckbox?.landings || []).find(
					(l) => l.id === landing.id
				);
				if (
					oldLanding &&
					JSON.stringify(landing.dimensions) !==
						JSON.stringify(oldLanding.dimensions)
				) {
					dispatch({ type: 'UPDATE_BLOCK', payload: landing });
				}
			});

			(updatedDeckbox.stairs || []).forEach((stair) => {
				if (stair.id === updatedBlock.id) return;
				const oldStair = (oldDeckbox?.stairs || []).find(
					(s) => s.id === stair.id
				);
				if (
					oldStair &&
					JSON.stringify(stair.dimensions) !==
						JSON.stringify(oldStair.dimensions)
				) {
					dispatch({ type: 'UPDATE_BLOCK', payload: stair });
				}
			});
		}
		return;
	}

	if (!parentAfterCascade) return;

	// Handle Deletions
	const idsToRemove = (parentAfterCascade as any)._idsToRemove || [];
	idsToRemove.forEach((id: string) => {
		const findBlockRecursive = (
			blks: (Block | BlockV3)[]
		): Block | BlockV3 | undefined => {
			for (const b of blks) {
				if (b.id === id) return b;
				const nested = [
					...(b.stairs || []),
					...(b.landings || []),
					...(b.bumpouts || []),
				];
				const found = findBlockRecursive(nested);
				if (found) return found;
			}
			return undefined;
		};

		const blockToDelete = findBlockRecursive(blocks);
		if (blockToDelete) {
			dispatch({ type: 'DELETE_BLOCK', payload: blockToDelete });
		}
	});

	// Handle Additions
	const itemsToAdd = (parentAfterCascade as any)._itemsToAdd || [];
	itemsToAdd.forEach((item: any) => {
		if (item.type === 'LANDING') {
			dispatch({ type: 'ADD_LANDING', payload: item.block });
		} else if (item.type === 'STAIR') {
			dispatch({ type: 'ADD_STAIR', payload: item.block });
		}
	});

	const updatedStairs = (parentAfterCascade as Block).stairs || [];
	updatedStairs.forEach((stair) => {
		const oldStair = (oldBlock as Block).stairs?.find((s) => s.id === stair.id);
		if (
			oldStair &&
			JSON.stringify(stair.dimensions) !== JSON.stringify(oldStair.dimensions)
		) {
			console.log('[updateChildBlocks] Updating Stair:', stair.id);
			dispatch({ type: 'UPDATE_BLOCK', payload: stair });
		}
	});

	const updatedLandings = (parentAfterCascade as Block).landings || [];
	updatedLandings.forEach((landing) => {
		const oldLanding = (oldBlock as Block).landings?.find(
			(l) => l.id === landing.id
		);
		if (
			oldLanding &&
			JSON.stringify(landing.dimensions) !==
				JSON.stringify(oldLanding.dimensions)
		) {
			console.log('[updateChildBlocks] Updating Landing:', landing.id);
			dispatch({ type: 'UPDATE_BLOCK', payload: landing });
		}
	});
};

export default updateChildBlocks;
